/**
 * The executive overview — altitude L0 (dashboard design rules §1/§7).
 *
 * One screen that answers "is anything wrong right now?" across every
 * monitored domain: a KPI tile per headline fact (each a link into its L1
 * board), a 14-day worst-status pulse strip, and the three newest incidents.
 * No tables, no charts, no scrolling — anything deeper lives one click away on
 * the boards.
 *
 * Every number is read through the domain boards' existing hooks; this page
 * adds no endpoints of its own. Feeds fail independently, and each panel
 * renders its own three-state truth (unconfigured ≠ empty ≠ zero).
 *
 * **Coverage is the point.** The row covers the domain boards in
 * `nav-sections.ts`, including the ones that are dark today (Network, Security,
 * Dynamics F&O). A board with no tile reads as an estate that is
 * smaller, healthier and cheaper than the real one, with no hint that it is
 * doing so — the failure §8 is written to prevent. A dark board therefore gets
 * a tile that says *why* it is dark, in a form that cannot be mistaken for a
 * healthy zero.
 *
 * Three tiles arrived with the F&O observability work: slow interactions (24 h)
 * with the layer that caused them, browser coverage across the reporting fleet,
 * and the open user-report queue. Each links to the board that draws the figure
 * — `/dynamics`, `/experience`, `/dynamics/reports` — never to F&O in general.
 *
 * Security holds one tile, not two. It carried a second ("Reached a user", on
 * the retired `/proofpoint` board) until the 2026-08-20 merge — two doors onto
 * one TAP credential. Defender and AI are still tile-less; that gap predates
 * this merge and is tracked in TODO 5.26, not closed here.
 *
 * **Why all fifteen queries fire eagerly.** Every route behind them is served
 * from a backend cache or store, so a page load spends no upstream vendor's
 * rate limit: Cato from the backend's one-per-minute snapshot, both TAP boards
 * from stored daily rollups, the two F&O routes from snapshots governed by
 * their own `cache_ttl_sec`, the console's slow summary and RUM status likewise,
 * the report queue from Postgres. That is the same test `lib/api/prefetch.ts`
 * already applies before it warms a board. Lazy-loading was considered and
 * rejected for a specific reason: a tile that only arrives on scroll or hover
 * is a tile that can be absent from a screenshot, and an absent honesty tile
 * defeats the entire purpose of adding it. The cost is real though — fifteen
 * parallel GETs will queue behind each other over HTTP/1.1 — so if first paint
 * becomes a problem the fix is a single aggregating `/overview` route on the
 * backend, not a lazier client.
 */

import { Alert, Freshness, PageHeader } from "@norican/react";

import { DEFAULT_REPORT_LOOKBACK } from "@/features/dynamics/console-format";
import {
  useCloudAlerts,
  useCloudCost,
  useCloudOverview,
  useFoConsoleReports,
  useFoConsoleRumStatus,
  useFoConsoleSlowSummary,
  useFoIntegration,
  useFoOverview,
  useLicenseOverview,
  useM365ServiceHealth,
  useMonitoringHistory,
  useMonitoringOverview,
  useNetworkOverview,
  useSecurityOverview,
} from "@/lib/api";
import { PRODUCT_NAME, PRODUCT_TAGLINE } from "@/lib/brand";

import { DomainTiles } from "./domain-tiles";
import { EstatePulse } from "./estate-pulse";
import { IncidentFeed } from "./incident-feed";

/** The pulse strip's window — two weeks, per the L0 recipe. */
const PULSE_DAYS = 14;

export function ExecutiveOverviewPage() {
  const cloud = useCloudOverview();
  const cost = useCloudCost();
  const alerts = useCloudAlerts();
  const licences = useLicenseOverview();
  const websites = useMonitoringOverview();
  const history = useMonitoringHistory(PULSE_DAYS);
  const m365 = useM365ServiceHealth();
  const network = useNetworkOverview();
  const security = useSecurityOverview();
  // Two F&O queries, deliberately. The telemetry workspace and the Service Bus
  // workspace carry independent `access_state`s and are unreadable in different
  // ways today — a 403 needing a role assignment and a 404 needing a corrected
  // resource ID. Folding them into one tile would send half the readers to the
  // wrong person.
  const foTelemetry = useFoOverview();
  const foIntegration = useFoIntegration();
  // The three F&O observability figures. Fixed windows on purpose: this page has
  // no filter row, and a tile whose window moved with someone else's board would
  // stop meaning what its caption says. 24 h for the console (the shortest
  // window a manager reads as "now"), the RUM status' own 24 h, and the report
  // store's 7-day default — which is also the window the sidebar's gate probe
  // reads, so the queue is fetched once for the whole session.
  const foSlow = useFoConsoleSlowSummary("24h");
  const foRum = useFoConsoleRumStatus();
  const foReports = useFoConsoleReports(DEFAULT_REPORT_LOOKBACK, null);

  const feeds = [
    cloud,
    cost,
    alerts,
    licences,
    websites,
    history,
    m365,
    network,
    security,
    foTelemetry,
    foIntegration,
    foSlow,
    foRum,
    // `foReports` is deliberately absent from this list: a 403 off the
    // investigator list is a normal answer for most readers, and counting it as
    // a failed feed would put "Couldn't load the overview" on a working page.
  ];
  const allFailed = feeds.every((feed) => feed.isError);
  const refreshing = feeds.some((feed) => feed.isFetching);

  // The header names the OLDEST read across the feeds — the most honest single
  // freshness figure a composed page can show. Per-panel freshness/coverage
  // lives on the panels themselves. A feed that has never succeeded reports
  // `null` here and drops out rather than dragging the figure to the epoch.
  const readTimes = [
    cloud.data?.collected_at,
    alerts.data?.collected_at,
    licences.data?.generated_at,
    websites.data?.summary.last_checked_at,
    m365.data?.last_updated_at,
    network.data?.summary.fetched_at,
    security.data?.health.last_success_at,
    foTelemetry.data?.collected_at,
    foIntegration.data?.collected_at,
    foSlow.data?.collected_at,
    foRum.data?.last_seen_at,
  ]
    .filter((value): value is string => value != null)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  const oldestRead = readTimes[0];

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <PageHeader
        title={PRODUCT_NAME}
        subtitle={PRODUCT_TAGLINE}
        actions={
          oldestRead ? (
            <Freshness label="Oldest feed" since={oldestRead} pulse={refreshing} />
          ) : undefined
        }
      />

      {allFailed && (
        <Alert tone="danger" title="Couldn't load the overview">
          The NOVA API did not respond for any feed. This is a NOVA problem, not a problem with the
          monitored services.
        </Alert>
      )}

      <DomainTiles
        cloud={cloud}
        cost={cost}
        licences={licences}
        websites={websites}
        m365={m365}
        network={network}
        security={security}
        foTelemetry={foTelemetry}
        foIntegration={foIntegration}
        foSlow={foSlow}
        foRum={foRum}
        foReports={foReports}
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <EstatePulse history={history} />
        </div>
        <IncidentFeed alerts={alerts} m365={m365} />
      </div>
    </main>
  );
}
